import { useQuery } from '@apollo/client';
import { Store } from '@website/Store';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import {
  GroupMediaDocument,
  GroupMediaQuery,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Head from 'next/head';
import { useContext } from 'react';
import { Alert } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroller';
import UploadMediaToTransLoadIt from '../UploadMediaToTransLoadIt';
import MediaGallery from './MediaGallery';

type Props = {
  groupID: string;
};

export default function GuestsGroupMedia({ groupID }: Props) {
  const { state } = useContext(Store);

  const { data, loading, error, fetchMore } = useQuery(GroupMediaDocument, {
    variables: { id: groupID },
    fetchPolicy: 'cache-and-network',
  });

  const nextToken = data?.group?.media?.nextToken;

  async function loadMoreImages() {
    await fetchMore({
      variables: {
        nextToken,
      },
      // After various attempt trying to use typePolicies, which at a
      // cache level, they introduced various issues that I decided
      // for the time being to fallback on updateQuery, which
      // unfortunately will be deprecated in future and replaced by
      // typePolicies. Hopefully, the issues around polling and
      // pagination will have been fixed by then.
      updateQuery: (
        prev: GroupMediaQuery,
        { fetchMoreResult }: { fetchMoreResult: GroupMediaQuery },
      ): GroupMediaQuery => {
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          ...prev,
          group: {
            ...prev.group,
            media: {
              nextToken: fetchMoreResult.group.media.nextToken,
              items: [
                ...prev.group.media.items,
                ...fetchMoreResult.group.media.items,
              ],
            },
          },
        };
      },
    });
  }

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  const startDate = DateTime.fromISO(data?.group?.start.utc, {
    zone: 'utc',
  })
    .setZone(data?.group.start.zone)
    .toFormat('DDD');

  return (
    <>
      <Head>
        <title>
          Photos for {data.group.trip.title} on {startDate}
        </title>
      </Head>

      <h3 className="mb-5 h2">
        Photos for {data.group.trip.title} on {startDate}
      </h3>

      <h4 className="mb-5 h4">Upload your photos</h4>

      <UploadMediaToTransLoadIt
        groupID={data.group.id}
        tripID={data.group.trip.id}
        allowMultipleSelect
        userID={state.user.id}
      />

      <h4 className="mb-5 mt-5 h4">Library</h4>

      {data.group?.media?.items?.length > 0 ? (
        <InfiniteScroll
          pageStart={1}
          loadMore={loadMoreImages}
          hasMore={!!nextToken}
          loader={<LoadingPage />}
          useWindow={false}
        >
          <MediaGallery
            images={data.group?.media?.items}
            xs={4}
            md={3}
            colClasses="p-2"
          />
        </InfiniteScroll>
      ) : (
        <Alert variant="warning">
          <em>No photos have been uploaded.</em>
        </Alert>
      )}
    </>
  );
}
